import { useState, useRef } from 'react'
import { motion, AnimatePresence, useInView } from 'framer-motion'
import { SPRING } from '@/lib/motion'
import { callAI, ASHU_SYSTEM_PROMPT } from '@/lib/ai'

const FAQS = [
  {
    q: 'How do I book a cleaning service?',
    amharic: 'የፅዳት አገልግሎት እንዴት ልቅድ?',
    a: 'Simply call +251 99 774 5317 or use our contact form. We offer same-day booking for urgent needs and flexible scheduling for regular cleaning.'
  },
  {
    q: 'What areas in Addis Ababa do you cover?',
    amharic: 'በአዲስ አበባ የትኞችን ቦታዎች ትሸፍናላችሁ?',
    a: 'We cover all neighborhoods including Bole, Kazanchis, Sarbet, Megenagna, CMC, Ayat, Summit, and surrounding areas. No location is too far!'
  },
  {
    q: 'How much does a home deep clean cost?',
    amharic: 'የቤት ጥልቅ ፅዳት ምን ያህል ያስከፍላል?',
    a: 'Pricing depends on the size of your space and specific needs. Call us for a free quote - we offer competitive rates and guaranteed satisfaction.'
  },
  {
    q: 'Do you bring your own equipment?',
    amharic: 'ራሳችሁን መሳሪያ ታመጣላችሁ?',
    a: 'Yes! Our teams come fully equipped with professional-grade cleaning supplies, machines, and eco-friendly products. You do not need to provide anything.'
  },
  {
    q: 'Is your service guaranteed?',
    amharic: 'እርግጠኛነት አለ?',
    a: 'Absolutely. If you are not 100% satisfied, we will return and re-clean at no extra charge. Your satisfaction is our priority.'
  },
  {
    q: 'Can I schedule regular cleaning?',
    amharic: 'የየጊዜ ፅዳት ልቅድ?',
    a: 'Yes! We offer weekly, bi-weekly, and monthly cleaning packages with discounted rates for recurring customers.'
  },
  {
    q: 'How long does sofa cleaning take?',
    amharic: 'የሶፋ ፅዳት ምን ያህል ጊዜ ይወስዳል?',
    a: 'Typically 1-2 hours depending on the size and condition. Your sofa will be dry and ready to use within 4-6 hours.'
  },
  {
    q: 'Do you work on holidays?',
    amharic: 'በበዓላት ትሰራላችሁ?',
    a: 'Yes! We offer special holiday cleaning packages for Enkutatash, Gena, Fasika, and other celebrations. Book early as these dates fill up quickly!'
  }
]

function FAQItem({ item, index }: { item: (typeof FAQS)[0]; index: number }) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05, ...SPRING }}
      style={{
        borderBottom: '1px solid rgba(255,255,255,0.08)'
      }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '24px 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          textAlign: 'left'
        }}
      >
        <div>
          <div
            style={{
              fontFamily: 'DM Sans',
              fontWeight: 600,
              fontSize: '1.1rem',
              color: 'var(--white)'
            }}
          >
            {item.q}
          </div>
          <div
            style={{
              fontFamily: 'DM Sans',
              fontSize: '0.85rem',
              color: 'var(--lime)',
              marginTop: 4,
              opacity: 0.7
            }}
          >
            {item.amharic}
          </div>
        </div>
        <motion.span
          animate={{ rotate: isOpen ? 45 : 0 }}
          style={{
            fontSize: '1.5rem',
            color: 'var(--lime)',
            flexShrink: 0,
            marginLeft: 16
          }}
        >
          +
        </motion.span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ overflow: 'hidden' }}
          >
            <div
              style={{
                padding: '0 0 24px',
                fontFamily: 'DM Sans',
                fontSize: '1rem',
                color: 'var(--white-60)',
                lineHeight: 1.7,
                maxWidth: 800
              }}
            >
              {item.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}

export function FAQ() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-80px' })
  const [aiQuestion, setAiQuestion] = useState('')
  const [aiResponse, setAiResponse] = useState('')
  const [aiLoading, setAiLoading] = useState(false)

  const handleAskAI = async () => {
    if (!aiQuestion.trim()) return
    setAiLoading(true)
    const response = await callAI(ASHU_SYSTEM_PROMPT, [
      { role: 'user', content: aiQuestion }
    ])
    setAiResponse(response)
    setAiLoading(false)
  }

  return (
    <section
      ref={ref}
      style={{
        background: 'var(--black)',
        padding: 'var(--pad-section) max(5vw, 24px)'
      }}
    >
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          style={{ textAlign: 'center', marginBottom: 48 }}
        >
          <span
            style={{
              fontFamily: 'DM Sans',
              fontSize: '0.8rem',
              color: 'var(--lime)',
              letterSpacing: '0.15em',
              textTransform: 'uppercase'
            }}
          >
            Got Questions? · ጥያቄ አለህ?
          </span>
          <h2
            style={{
              fontFamily: 'Bebas Neue',
              fontSize: 'var(--h2)',
              color: 'var(--white)',
              marginTop: 12
            }}
          >
            Frequently Asked
          </h2>
        </motion.div>

        {/* FAQ List */}
        <div style={{ marginBottom: 64 }}>
          {FAQS.map((item, i) => (
            <FAQItem key={i} item={item} index={i} />
          ))}
        </div>

        {/* AI Assistant */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          style={{
            background: 'var(--surface)',
            border: '1px solid rgba(232,255,0,0.15)',
            borderRadius: 20,
            padding: 32
          }}
        >
          <div
            style={{
              fontFamily: 'DM Sans',
              fontWeight: 600,
              fontSize: '1.1rem',
              color: 'var(--white)',
              marginBottom: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: 'var(--lime)',
                animation: 'pulse 2s infinite'
              }}
            />
            Ask Ashu AI · AI አሹን ጠይቅ
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <input
              type="text"
              value={aiQuestion}
              onChange={(e) => setAiQuestion(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAskAI()}
              placeholder="Ask anything about our services..."
              style={{
                flex: 1,
                padding: '14px 18px',
                background: 'var(--elevated)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 12,
                color: 'var(--white)',
                fontFamily: 'DM Sans',
                fontSize: '1rem',
                outline: 'none'
              }}
            />
            <motion.button
              onClick={handleAskAI}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={aiLoading}
              style={{
                padding: '14px 24px',
                background: 'var(--lime)',
                border: 'none',
                borderRadius: 12,
                color: 'var(--black)',
                fontFamily: 'DM Sans',
                fontWeight: 700,
                cursor: aiLoading ? 'not-allowed' : 'pointer',
                opacity: aiLoading ? 0.7 : 1
              }}
            >
              {aiLoading ? '...' : 'Ask'}
            </motion.button>
          </div>

          <AnimatePresence>
            {aiResponse && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                style={{
                  padding: 16,
                  background: 'var(--elevated)',
                  borderRadius: 12,
                  fontFamily: 'DM Sans',
                  color: 'var(--white-60)',
                  lineHeight: 1.6
                }}
              >
                {aiResponse}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  )
}
